import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Fade,
    Typography,
} from "@mui/material";
import MarkEmailReadRoundedIcon from "@mui/icons-material/MarkEmailReadRounded";
import NotificationsOffRoundedIcon from "@mui/icons-material/NotificationsOffRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import axios from "axios";
import { useLocation } from "react-router-dom";

const UnsubscribePage = () => {
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [unsubscribed, setUnsubscribed] = useState(false);
    const [userData, setUserData] = useState({
        email: "",
        name: "",
        suitCode: "",
    });


    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const email = queryParams.get("email");
        const name = queryParams.get("name");
        const suitCode = queryParams.get("suitCode");

        setUserData({
            email: email || "",
            name: name || "",
            suitCode: suitCode || "",
        });
    }, []);

    const handleUnsubscribe = async () => {
        try {
            setLoading(true);
            const payload = {
                email: userData?.email,
                name: userData?.name,
                suitCode: userData?.suitCode,
            };
            // API Call
            const result = await axios.post("https://dev.ienergydigital.com/task-api/api/email-suits/unsubscribe", payload);
            if (result.response.success) {
                setUnsubscribed(true);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };



    return (
        <Box
            sx={{
                minHeight: "80vh",
                background:
                    "linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #f1f5f9 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                py: 4,
            }}
        >
            <Container maxWidth="sm">
                <Fade in timeout={700}>
                    <Card
                        elevation={0}
                        sx={{
                            borderRadius: "28px",
                            overflow: "hidden",
                            backdropFilter: "blur(10px)",
                            border: "1px solid #e2e8f0",
                            boxShadow: "0 10px 40px rgba(0,0,0,0.08)",
                        }}
                    >
                        {/* Top Section */}
                        <Box
                            sx={{
                                background:
                                    unsubscribed
                                        ? "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
                                        : "linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)",
                                p: 5,
                                textAlign: "center",
                                color: "#fff",
                            }}
                        >
                            <Box
                                sx={{
                                    width: 80,
                                    height: 80,
                                    borderRadius: "50%",
                                    background: "rgba(255,255,255,0.15)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    margin: "0 auto 20px",
                                    backdropFilter: "blur(8px)",
                                }}
                            >
                                {unsubscribed ? (
                                    <NotificationsOffRoundedIcon sx={{ fontSize: 45 }} />
                                ) : (
                                    <MarkEmailReadRoundedIcon sx={{ fontSize: 45 }} />
                                )}
                            </Box>

                            <Typography
                                variant="h5"
                                fontWeight={800}
                                gutterBottom
                            >
                                {unsubscribed
                                    ? "You’ve Been Unsubscribed"
                                    : "Unsubscribe from Emails"}
                            </Typography>

                            <Typography
                                sx={{
                                    opacity: 0.9,
                                    maxWidth: 420,
                                    margin: "0 auto",
                                    lineHeight: 1.7,
                                    fontSize: 15,
                                }}
                            >
                                {unsubscribed
                                    ? "You will no longer receive emails, newsletters, updates, or promotional communication from this source."
                                    : "Are you sure you want to stop receiving emails from this sender?"}
                            </Typography>
                        </Box>

                        {/* Content */}
                        <CardContent sx={{ p: 4 }}>
                            {!unsubscribed ? (
                                <>
                                    <Box
                                        sx={{
                                            background: "#f8fafc",
                                            border: "1px solid #e2e8f0",
                                            borderRadius: "18px",
                                            p: 3,
                                            mb: 3,
                                        }}
                                    >
                                        <Box
                                            display="flex"
                                            alignItems="flex-start"
                                            gap={2}
                                        >
                                            <ErrorOutlineRoundedIcon
                                                sx={{
                                                    color: "#f59e0b",
                                                    mt: "2px",
                                                }}
                                            />

                                            <Box>
                                                <Typography
                                                    fontWeight={700}
                                                    mb={1}
                                                >
                                                    Before You Leave
                                                </Typography>

                                                <Typography
                                                    color="text.secondary"
                                                    fontSize={14}
                                                    lineHeight={1.8}
                                                >
                                                    If you unsubscribe, you may miss important updates,
                                                    account notifications, product announcements, and
                                                    future offers related to your activity.
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>

                                    <Divider sx={{ mb: 3 }} />

                                    <Box
                                        display="flex"
                                        gap={2}
                                        flexWrap="wrap"
                                    >
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            disabled={loading}
                                            onClick={handleUnsubscribe}
                                            sx={{
                                                height: 44,
                                                borderRadius: "14px",
                                                textTransform: "none",
                                                fontSize: 16,
                                                fontWeight: 700,
                                                background:
                                                    "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                                                boxShadow: "none",
                                                "&:hover": {
                                                    boxShadow: "none",
                                                },
                                            }}
                                        >
                                            {loading ? (
                                                <CircularProgress
                                                    size={24}
                                                    sx={{ color: "#fff" }}
                                                />
                                            ) : (
                                                "Yes, Unsubscribe Me"
                                            )}
                                        </Button>

                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            sx={{
                                                height: 44,
                                                borderRadius: "14px",
                                                textTransform: "none",
                                                fontSize: 16,
                                                fontWeight: 700,
                                                borderColor: "#cbd5e1",
                                                color: "#111827",
                                            }}
                                            onClick={() => window.history.back()}
                                        >
                                            Cancel
                                        </Button>
                                    </Box>
                                </>
                            ) : (
                                <Box textAlign="center">
                                    <Typography
                                        variant="h6"
                                        fontWeight={700}
                                        gutterBottom
                                    >
                                        Subscription Removed Successfully
                                    </Typography>

                                    <Typography
                                        color="text.secondary"
                                        lineHeight={1.8}
                                        mb={4}
                                    >
                                        Your email preference has been updated successfully.
                                        You won’t receive future communication from this source.
                                    </Typography>

                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Fade>
            </Container>

           
        </Box>
    );
};

export default UnsubscribePage;